import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { HeaderNavItem } from '../../types/headerNav';
import { createEmptyNavItem } from '../../types/headerNav';
import { getNavHref, isExternalNav, sortNavItems } from '../../utils/headerNavUtils';
import { HeaderNavIcon } from '../../utils/headerNavIcons';
import { getImageUrl } from '../../utils/imageUtils';

interface Props {
  items: HeaderNavItem[];
  fallbackCategories?: { label: string; slug: string }[];
  /** Admin preview — show bar even on large breakpoints */
  preview?: boolean;
}

function nestedEntries(item: HeaderNavItem) {
  const childItems = sortNavItems(item.children || []);
  const columnGroups =
    item.menuType === 'mega' && item.megaMenu?.columns?.length
      ? item.megaMenu.columns.map((col) => ({
          title: col.title,
          items: sortNavItems(col.items || []),
        }))
      : [];
  return { childItems, columnGroups };
}

function hasNestedMenu(item: HeaderNavItem): boolean {
  const { childItems, columnGroups } = nestedEntries(item);
  return childItems.length > 0 || columnGroups.some((g) => g.items.length > 0);
}

function flattenLinks(item: HeaderNavItem, depth = 0): { item: HeaderNavItem; depth: number }[] {
  const out: { item: HeaderNavItem; depth: number }[] = [];
  const { childItems, columnGroups } = nestedEntries(item);

  for (const child of childItems) {
    if (hasNestedMenu(child)) {
      out.push({ item: child, depth });
      out.push(...flattenLinks(child, depth + 1));
    } else {
      out.push({ item: child, depth });
    }
  }
  for (const col of columnGroups) {
    for (const sub of col.items) {
      out.push({ item: sub, depth });
    }
  }
  return out;
}

const HeaderNavMobileBar: React.FC<Props> = ({ items, fallbackCategories = [], preview = false }) => {
  const location = useLocation();
  const [openId, setOpenId] = useState<string | null>(null);

  const navItems = useMemo((): HeaderNavItem[] => {
    const enabled = sortNavItems(items.filter((i) => i.isEnabled !== false));
    if (enabled.length) return enabled;
    return fallbackCategories.map((c, i) =>
      createEmptyNavItem({
        id: c.slug || `fb-${i}`,
        label: c.label,
        url: c.slug ? `/category/${c.slug}` : '/',
        linkType: 'category',
        categorySlug: c.slug,
        order: i,
        isEnabled: true,
        menuType: 'link',
      })
    );
  }, [items, fallbackCategories]);

  useEffect(() => {
    setOpenId(null);
  }, [location.pathname]);

  const toggleParent = (item: HeaderNavItem) => {
    if (!hasNestedMenu(item)) return;
    setOpenId((prev) => (prev === item.id ? null : item.id));
  };

  const renderSubLink = (item: HeaderNavItem, depth: number) => {
    const href = getNavHref(item);
    const pad = { paddingLeft: `${12 + depth * 12}px` };
    const inner = (
      <div className="flex items-center gap-2.5 min-w-0 py-2.5 pr-3" style={pad}>
        {(item.thumbnailUrl || item.imageUrl) && (
          <img
            src={getImageUrl(item.thumbnailUrl || item.imageUrl)}
            alt=""
            className="w-7 h-7 rounded-md object-cover shrink-0 bg-gray-100"
          />
        )}
        {!item.thumbnailUrl && !item.imageUrl && item.icon && (
          <HeaderNavIcon name={item.icon} className="w-4 h-4 text-gray-500 shrink-0" />
        )}
        <span className="flex-1 text-sm text-gray-800 truncate">{item.label}</span>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
      </div>
    );

    if (isExternalNav(item)) {
      return (
        <a
          key={item.id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block border-b border-gray-100 last:border-0 active:bg-gray-50"
        >
          {inner}
        </a>
      );
    }

    return (
      <Link
        key={item.id}
        to={href}
        className="block border-b border-gray-100 last:border-0 active:bg-gray-50"
        onClick={() => setOpenId(null)}
      >
        {inner}
      </Link>
    );
  };

  const openItem = openId ? navItems.find((i) => i.id === openId) : null;

  return (
    <div className={`${preview ? '' : 'lg:hidden '}border-t border-gray-100 bg-white min-w-0 w-full`}>
      <div className="header-nav-mobile-bar-viewport min-w-0 w-full">
        <div
          className="header-nav-mobile-bar-track flex gap-2 px-3 py-2.5"
          role="navigation"
          aria-label="Store navigation"
        >
        {navItems.map((item) => {
          const href = getNavHref(item);
          const nested = hasNestedMenu(item);
          const isOpen = openId === item.id;
          const isActive = !nested && !isExternalNav(item) && location.pathname === href;

          if (nested) {
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleParent(item)}
                aria-expanded={isOpen}
                className={`header-nav-mobile-bar-chip shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                  isOpen
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-800 active:bg-gray-200'
                }`}
              >
                {item.icon && !isOpen && <HeaderNavIcon name={item.icon} className="w-3.5 h-3.5 shrink-0" />}
                <span className="whitespace-nowrap">{item.label}</span>
                {item.badge && (
                  <span
                    className="text-[9px] font-bold px-1 rounded text-white"
                    style={{ backgroundColor: item.badgeColor || '#ef4444' }}
                  >
                    {item.badge}
                  </span>
                )}
                <ChevronDown
                  className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
            );
          }

          if (isExternalNav(item)) {
            return (
              <a
                key={item.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="header-nav-mobile-bar-chip shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
              >
                <span className="whitespace-nowrap">{item.label}</span>
              </a>
            );
          }

          return (
            <Link
              key={item.id}
              to={href}
              className={`header-nav-mobile-bar-chip shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                isActive ? 'bg-primary-600 text-white shadow-sm' : 'bg-gray-100 text-gray-800 active:bg-gray-200'
              }`}
            >
              {item.icon && <HeaderNavIcon name={item.icon} className="w-3.5 h-3.5 shrink-0" />}
              <span className="whitespace-nowrap">{item.label}</span>
              {item.badge && (
                <span
                  className="text-[9px] font-bold px-1 rounded text-white"
                  style={{ backgroundColor: item.badgeColor || '#ef4444' }}
                >
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
        </div>
      </div>

      {openItem && hasNestedMenu(openItem) && (
        <div className="border-t border-gray-100 bg-slate-50/90 px-1 pb-2 max-h-[min(50vh,320px)] overflow-y-auto overscroll-contain ">
          {flattenLinks(openItem).map(({ item, depth }) => renderSubLink(item, depth))}
          {openItem.megaMenu?.promoImage && (
            <Link
              to={openItem.megaMenu.promoLink || '#'}
              className="block p-2"
              onClick={() => setOpenId(null)}
            >
              <img
                src={getImageUrl(openItem.megaMenu.promoImage)}
                alt={openItem.megaMenu.promoTitle || ''}
                className="w-full max-h-20 object-cover rounded-lg"
              />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderNavMobileBar;
